import type { Plural } from '../../core.js';

const p = (forms: Plural): Plural => forms;

/** Texts of the PDF design report (tables from the model and the page layout). Part of `model`. */
export const report = {
  title: 'Entwurfsbericht',
  /** Part of the file name; only letters, digits and hyphens. */
  fileWord: 'Entwurfsbericht',
  disclaimer: 'Konzept-/Entwurfsplanung – keine Ausführungs-, Genehmigungs- oder Statikplanung.',
  header: '{title} · Stand {date} · Revision {revision}',
  page: 'Seite {page} von {total}',
  createdBy: 'Entwurfsbericht vom {date} · erstellt von {author}',
  description: 'Projektbeschreibung',
  figuresTitle: 'Kennzahlen',
  noView: 'Keine 3D-Ansicht übermittelt.',
  plan: 'Grundriss',
  planLevel: 'Grundriss {level}',
  nothingPlanned: 'Noch nichts geplant.',
  scale: 'Maßstab 1:{scale}',
  scaleHint: 'bei Druck auf A4 ohne Skalierung',
  legendTitle: 'Legende',
  unitsMeters: 'Maße in Metern',
  units: 'Maße in {unit}',
  north: 'N',
  view: '3D-Ansicht {n}',
  views: { southEast: 'Perspektive von Südosten', northWest: 'Perspektive von Nordwesten' },
  tablesTitle: 'Flächen und Bauteile',
  tablesContinued: 'Flächen und Bauteile (Fortsetzung)',
  notesTitle: 'Hinweise aus der Planung',
  notesHint:
    'Die Hinweise sind geometrische Plausibilitätsprüfungen und ersetzen keine Fachplanung.',
  total: 'Gesamt',
  sum: 'Summe',
  roofTypes: { gable: 'Satteldach', mono: 'Pultdach', flat: 'Flachdach' },
  figures: {
    site: 'Grundstück',
    parcels: 'Flurstücke',
    built: 'Bebaute Fläche',
    coverage: 'Ausnutzung (bebaut / Grundstück)',
    free: 'Freie Grundstücksfläche',
    halls: 'Hallen',
    offices: 'Bürogebäude',
    officeArea: 'Bürofläche (BGF, alle Geschosse)',
    traffic: 'Verkehrsflächen',
    carStalls: 'PKW-Stellplätze',
    truckStalls: 'LKW-Stellplätze',
    fence: 'Zaunlänge',
    palletPlaces: 'Palettenplätze',
    shelfPlaces: 'Fachboden-Stellplätze',
    shelfArea: 'Fachbodenfläche',
  },
  parcels: {
    title: 'Grundstück (Flurstücke)',
    columns: ['Flurstück', 'Fläche', 'Umfang'],
  },
  offices: {
    title: 'Bürogebäude',
    columns: [
      'Gebäude',
      'Lage',
      'Außenmaße',
      'Geschosse (lichte Höhe)',
      'Decke',
      'Gesamthöhe',
      'BGF',
    ],
    indoor: 'Einbau',
    indoorIn: 'Einbau in {hall}',
    freestanding: 'freistehend',
    usableTop: 'Decke als Empore nutzbar',
  },
  roomBook: {
    title: 'Raumbuch {name}',
    columns: ['Geschoss', 'Raum', 'Raumart', 'Fläche', 'Lichte Höhe'],
    unassigned: 'nicht zugeordnet',
    rooms: p({ one: '{n} Raum', other: '{n} Räume' }),
  },
  stairs: {
    title: 'Treppen',
    columns: [
      'Treppe',
      'Gebäude',
      'Verbindung',
      'Laufbreite',
      'Steigungen',
      'Steigung / Auftritt',
      'Grundfläche',
    ],
    enclosed: '{name} (Treppenhaus)',
    landing: '{name}, Podest {length}',
    ratio: '{riser} / {going} cm',
  },
  halls: {
    title: 'Hallen',
    freeOutline: 'Freie Form ({n} Ecken)',
    columns: [
      'Halle',
      'Außenmaße',
      'Fläche',
      'Nutzfläche innen',
      'Traufe / First',
      'Dach',
      'Öffnungen',
    ],
  },
  mezzanines: {
    title: 'Geschossflächen in Hallen (1. OG)',
    columns: ['Geschossfläche', 'Halle', 'Höhe OK Fußboden', 'Decke', 'Ausstattung', 'Fläche'],
    railing: 'Geländer',
    columns_: 'Stützen',
  },
  mezzanineAreas: {
    title: 'Aufteilung {name} (nach Wänden, ohne Wanddicken)',
    columns: ['Bereich', 'Fläche', 'Anteil'],
  },
  zones: {
    title: 'Flächenaufteilung {name}',
    columns: ['Bereich', 'Art', 'Fläche', 'Anteil'],
    room: 'Raum',
    unplanned: 'noch nicht verplant',
    interior: 'Nutzfläche innen',
  },
  sections: {
    title: 'Hallenbereiche {name} (durch Trennwände, gemessen bis Wandachse)',
    columns: ['Bereich', 'Fläche', 'Anteil'],
  },
  outdoorZones: {
    title: 'Außenflächen',
    columns: ['Fläche', 'Art', 'Größe'],
  },
  openings: {
    title: 'Tore, Türen und Fenster',
    columns: ['Halle', 'Typ', 'Maße (B × H)', 'Anzahl'],
    interiorWalls: 'Innenwände',
    sill: 'Brüstung {length}',
    bay: 'Zufahrt {width} × {length}',
  },
  roof: {
    title: 'Dachelemente',
    columns: ['Halle', 'Element', 'Anzahl', 'Fläche'],
    daylight: 'Tageslichtanteil Dach',
  },
  objects: {
    title: 'Objekte',
    columns: ['Bezeichnung', 'Maße (B × T × H)', 'Anzahl'],
  },
  racks: {
    title: 'Palettenregale',
    columns: [
      'Regal',
      'Traverse',
      'Felder',
      'Fächer',
      'Plätze 1,60 m',
      'Plätze 0,80 m',
      'Palettenplätze',
    ],
  },
  shelves: {
    title: 'Fachbodenregale',
    columns: [
      'Regal',
      'Höhe',
      'Felder',
      'Fächer',
      'Stellplätze (Karton 30 × 60 cm)',
      'Fachbodenfläche',
    ],
  },
  facilities: {
    title: 'Außenanlagen',
    columns: ['Bezeichnung', 'Art', 'Maße', 'Fläche'],
    road: '{width} breit, {length} lang',
    parking: '{stall}, Gasse {aisle}',
    fence: 'Zaun',
    fenceSize: '{length} lang, {height} hoch',
  },
  legend: {
    site: 'Grundstücksgrenze',
    parcel: 'Flurstücksgrenze',
    hall: 'Halle / Außenwand',
    room: 'Raum',
    dock: 'Andockzufahrt (abgesenkt)',
    object: 'Objekt',
    clearance: 'Schutz-/Bedienabstand',
    roofElement: 'Dachelement (darüberliegend)',
    road: 'Straße / Weg',
    parking: 'Parkfläche',
    fence: 'Zaun',
    measure: 'Maß',
    stair: 'Treppe (Pfeil = aufwärts)',
  },
};

export type ReportMessages = typeof report;
